// Connections content component for the account page

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { RefreshCwIcon } from 'lucide-react';
import { analyticsService } from '../../../services/analyticsService';
import {
  getTwitchStatus,
  initiateTwitchLink,
} from '../../../transport/api/protectedApi';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import type { UserAuthFields } from '@airhead-games/shared/types';

type ConnectionsContentProps = {
  user: UserAuthFields;
  connectionsTwitchError: string | null;
};

type ConnectionStatus =
  'disconnected' | 'checking' | 'connected' | 'needsReconnect' | 'error';

type ConnectionRow = {
  id: 'twitch';
  serviceName: string;
  featureHref: string;
  featureLinkLabel: string;
  status: ConnectionStatus;
  connectedLogin?: string;
  errorMessage?: string | null;
  onConnect: () => void;
  onRefresh: () => void;
};

const ConnectionsContent: React.FC<ConnectionsContentProps> = ({
  user,
  connectionsTwitchError,
}) => {
  const [twitchExpired, setTwitchExpired] = useState(false);
  const [twitchStatusError, setTwitchStatusError] = useState<string | null>(
    null
  );
  const [checkingStatus, setCheckingStatus] = useState(false);

  useEffect(() => {
    if (!user.authTwitch?.login) {
      setTwitchExpired(false);
      setTwitchStatusError(null);
      return;
    }

    const checkTwitchStatus = async () => {
      setCheckingStatus(true);
      setTwitchStatusError(null);
      try {
        const response = await getTwitchStatus();
        if (response.success && response.data) {
          setTwitchExpired(!response.data.isValid);
        }
      } catch {
        setTwitchStatusError('Could not check Twitch connection status.');
      } finally {
        setCheckingStatus(false);
      }
    };

    void checkTwitchStatus();
  }, [user.authTwitch?.login]);

  const handleConnect = () => {
    analyticsService.trackButtonClick('Connect Twitch Account', {
      link_location: 'library_connections',
    });
    initiateTwitchLink({
      redirectTo: '/library?dialog=connections',
    });
  };

  const handleReconnect = () => {
    analyticsService.trackButtonClick('Refresh Twitch Connection', {
      link_location: 'library_connections',
      reconnect_reason: twitchExpired ? 'token_expired' : 'manual',
    });
    initiateTwitchLink({
      redirectTo: '/library?dialog=connections',
    });
  };

  const twitchStatus: ConnectionStatus =
    connectionsTwitchError || twitchStatusError
      ? 'error'
      : !user.authTwitch?.login
        ? 'disconnected'
        : checkingStatus
          ? 'checking'
          : twitchExpired
            ? 'needsReconnect'
            : 'connected';

  const connectionRows: ConnectionRow[] = [
    {
      id: 'twitch',
      serviceName: 'Twitch',
      featureHref: '/discover/features/twitch',
      featureLinkLabel: 'Twitch connection',
      status: twitchStatus,
      connectedLogin: user.authTwitch?.login,
      errorMessage: getConnectionErrorLabel(
        connectionsTwitchError ?? twitchStatusError
      ),
      onConnect: handleConnect,
      onRefresh: handleReconnect,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {connectionRows.map((row) => (
        <ConnectedAccountRow key={row.id} row={row} />
      ))}
    </div>
  );
};

function ConnectedAccountRow({ row }: { row: ConnectionRow }) {
  const showRefresh = row.status === 'needsReconnect' || row.status === 'error';
  const showConnectedLogin = row.status === 'connected' && row.connectedLogin;
  const showErrorMessage = row.status === 'error' && row.errorMessage;

  return (
    <div className="flex items-start justify-between gap-3 rounded-lg border p-4">
      <div className="min-w-0 space-y-0.5">
        <div className="flex h-6 items-center text-sm font-medium">
          {row.serviceName}
        </div>
        <p className="text-xs text-muted-foreground">
          Learn about{' '}
          <Link
            to={row.featureHref}
            className="text-primary underline-offset-4 hover:underline"
          >
            {row.featureLinkLabel}
          </Link>
        </p>
      </div>
      <div
        className={
          row.status === 'disconnected'
            ? 'ml-auto self-center'
            : 'ml-auto flex shrink-0 flex-col items-end gap-0.5'
        }
      >
        <div className="flex h-6 items-center gap-1">
          {showRefresh && (
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label={`Refresh ${row.serviceName} connection`}
              title={`Refresh ${row.serviceName} connection`}
              onClick={row.onRefresh}
            >
              <RefreshCwIcon />
            </Button>
          )}
          <ConnectionStatusBadge status={row.status} />
          {row.status === 'disconnected' && (
            <Button onClick={row.onConnect}>Connect</Button>
          )}
        </div>
        {showConnectedLogin && (
          <p className="text-right text-xs text-muted-foreground">
            {row.connectedLogin}
          </p>
        )}
        {showErrorMessage && (
          <p className="text-right text-xs text-destructive">
            {row.errorMessage}
          </p>
        )}
      </div>
    </div>
  );
}

function ConnectionStatusBadge({ status }: { status: ConnectionStatus }) {
  switch (status) {
    case 'checking':
      return (
        <Badge variant="secondary" className="ml-auto">
          Checking...
        </Badge>
      );
    case 'connected':
      return (
        <Badge variant="secondary" className="ml-auto">
          Connected
        </Badge>
      );
    case 'needsReconnect':
      return (
        <Badge variant="outline" className="ml-auto">
          Needs reconnect
        </Badge>
      );
    case 'error':
      return (
        <Badge variant="destructive" className="ml-auto">
          Error
        </Badge>
      );
    case 'disconnected':
      return null;
  }
}

function getConnectionErrorLabel(errorMessage: string | null): string | null {
  if (!errorMessage) {
    return null;
  }

  if (errorMessage.includes('already linked')) {
    return 'Linked to different user';
  }

  if (errorMessage.includes('email is already connected')) {
    return 'Email conflict';
  }

  if (errorMessage.includes('permissions were not granted')) {
    return 'Permission denied';
  }

  if (errorMessage.includes('does not match')) {
    return 'Account mismatch';
  }

  return 'Connection failed';
}

export default ConnectionsContent;
